/**
 * SurveyRepository - Repositório de Levantamentos
 * Persiste levantamentos no IndexedDB
 */

import { openDatabase } from './IndexedDBSetup';

const STORE_NAME = 'surveys';

const generateLocalId = () => {
  const now = new Date();
  const dateStr = now.toISOString().replace(/[-:T]/g, '').slice(0, 14);
  const random = Math.random().toString(36).substring(2, 5).toUpperCase();
  return `LOC-SURVEY-${dateStr}-${random}`;
};

export const create = async (surveyData) => {
  const db = await openDatabase();

  return new Promise((resolve, reject) => {
    const transaction = db.transaction([STORE_NAME], 'readwrite');
    const store = transaction.objectStore(STORE_NAME);

    const localId = generateLocalId();
    const now = new Date().toISOString();

    const survey = {
      ...surveyData,
      id: localId,
      localId,
      syncStatus: 'pending',
      createdAt: now,
      updatedAt: now,
      serverId: null,
    };

    const request = store.add(survey);

    request.onsuccess = () => {
      resolve(survey);
    };

    request.onerror = () => {
      reject(new Error(`Erro ao criar levantamento: ${request.error}`));
    };
  });
};

export const update = async (id, data) => {
  const db = await openDatabase();

  return new Promise((resolve, reject) => {
    const transaction = db.transaction([STORE_NAME], 'readwrite');
    const store = transaction.objectStore(STORE_NAME);

    const getRequest = store.get(id);

    getRequest.onsuccess = () => {
      const existing = getRequest.result;

      if (!existing) {
        reject(new Error(`Levantamento com ID ${id} não encontrado`));
        return;
      }

      const updated = {
        ...existing,
        ...data,
        id: existing.id,
        localId: existing.localId,
        syncStatus: 'pending',
        updatedAt: new Date().toISOString(),
      };

      const putRequest = store.put(updated);

      putRequest.onsuccess = () => resolve(updated);
      putRequest.onerror = () => reject(new Error(`Erro ao atualizar levantamento: ${putRequest.error}`));
    };

    getRequest.onerror = () => reject(new Error(`Erro ao buscar levantamento: ${getRequest.error}`));
  });
};

export const remove = async (id) => {
  const db = await openDatabase();

  return new Promise((resolve, reject) => {
    const transaction = db.transaction([STORE_NAME], 'readwrite');
    const store = transaction.objectStore(STORE_NAME);
    const request = store.delete(id);

    request.onsuccess = () => resolve();
    request.onerror = () => reject(new Error(`Erro ao remover levantamento: ${request.error}`));
  });
};

export const getById = async (id) => {
  const db = await openDatabase();

  return new Promise((resolve, reject) => {
    const transaction = db.transaction([STORE_NAME], 'readonly');
    const store = transaction.objectStore(STORE_NAME);
    const request = store.get(id);

    request.onsuccess = () => resolve(request.result || null);
    request.onerror = () => reject(new Error(`Erro ao buscar levantamento: ${request.error}`));
  });
};

export const getByLocalId = async (localId) => {
  const db = await openDatabase();

  return new Promise((resolve, reject) => {
    const transaction = db.transaction([STORE_NAME], 'readonly');
    const store = transaction.objectStore(STORE_NAME);
    const index = store.index('localId');
    const request = index.get(localId);

    request.onsuccess = () => resolve(request.result || null);
    request.onerror = () => reject(new Error(`Erro ao buscar levantamento: ${request.error}`));
  });
};

export const getByServerId = async (serverId) => {
  const db = await openDatabase();

  return new Promise((resolve, reject) => {
    const transaction = db.transaction([STORE_NAME], 'readonly');
    const store = transaction.objectStore(STORE_NAME);
    const index = store.index('serverId');
    const request = index.get(serverId);

    request.onsuccess = () => resolve(request.result || null);
    request.onerror = () => reject(new Error(`Erro ao buscar levantamento: ${request.error}`));
  });
};

export const getAll = async (options = {}) => {
  const db = await openDatabase();

  return new Promise((resolve, reject) => {
    const transaction = db.transaction([STORE_NAME], 'readonly');
    const store = transaction.objectStore(STORE_NAME);

    const results = [];
    const request = store.openCursor();

    request.onsuccess = (event) => {
      const cursor = event.target.result;

      if (cursor) {
        const survey = cursor.value;

        let include = true;

        if (options.syncStatus && survey.syncStatus !== options.syncStatus) {
          include = false;
        }

        if (options.clientName) {
          const name = (survey.client_name || '').toLowerCase();
          const search = options.clientName.toLowerCase();
          if (!name.includes(search)) {
            include = false;
          }
        }

        if (include) {
          results.push(survey);
        }

        cursor.continue();
      } else {
        results.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
        resolve(results);
      }
    };

    request.onerror = () => reject(new Error(`Erro ao listar levantamentos: ${request.error}`));
  });
};

export const getPending = async () => {
  return getAll({ syncStatus: 'pending' });
};

export const updateSyncStatus = async (id, status, serverId = null) => {
  const db = await openDatabase();

  return new Promise((resolve, reject) => {
    const transaction = db.transaction([STORE_NAME], 'readwrite');
    const store = transaction.objectStore(STORE_NAME);
    const getRequest = store.get(id);

    getRequest.onsuccess = () => {
      const existing = getRequest.result;
      if (!existing) {
        reject(new Error(`Levantamento com ID ${id} não encontrado`));
        return;
      }

      const updated = {
        ...existing,
        syncStatus: status,
        serverId: serverId !== null ? serverId : existing.serverId,
        syncedAt: status === 'synced' ? new Date().toISOString() : existing.syncedAt,
      };

      const putRequest = store.put(updated);
      putRequest.onsuccess = () => resolve(updated);
      putRequest.onerror = () => reject(new Error(`Erro ao atualizar sync status: ${putRequest.error}`));
    };

    getRequest.onerror = () => reject(new Error(`Erro ao buscar levantamento: ${getRequest.error}`));
  });
};

export const updateServerId = async (localId, serverId) => {
  const db = await openDatabase();

  return new Promise((resolve, reject) => {
    const transaction = db.transaction([STORE_NAME], 'readwrite');
    const store = transaction.objectStore(STORE_NAME);
    const index = store.index('localId');
    const getRequest = index.get(localId);

    getRequest.onsuccess = () => {
      const existing = getRequest.result;
      if (!existing) {
        reject(new Error(`Levantamento com localId ${localId} não encontrado`));
        return;
      }

      const updated = { ...existing, serverId, syncStatus: 'synced' };
      const putRequest = store.put(updated);
      putRequest.onsuccess = () => resolve(updated);
      putRequest.onerror = () => reject(new Error(`Erro ao atualizar serverId: ${putRequest.error}`));
    };

    getRequest.onerror = () => reject(new Error(`Erro ao buscar levantamento: ${getRequest.error}`));
  });
};

export const countPending = async () => {
  const pending = await getPending();
  return pending.length;
};

export default {
  create,
  update,
  remove,
  getById,
  getByLocalId,
  getByServerId,
  getAll,
  getPending,
  updateSyncStatus,
  updateServerId,
  countPending,
};
